// Evidence — a media artifact (photo, audio, note) attached to a finding.
//
// `url` is the canonical CDN URL after upload. For S3-style storage, the
// engine stays storage-agnostic — the URL is whatever media-svc returns,
// and the optional `objectKey` lets us re-derive the storage path for
// deletion / re-signing.
import { isoDateTimeSchema } from "@bitacorapro/shared";
import { z } from "zod";
import { evidenceTypeSchema } from "../enums.js";
import { evidenceIdSchema, findingIdSchema } from "../ids.js";
export const evidenceSchema = z.object({
    id: evidenceIdSchema,
    findingId: findingIdSchema,
    evidenceType: evidenceTypeSchema,
    url: z.string().url(),
    objectKey: z.string().max(512).optional(),
    // Free-form caption / note text. For evidenceType="note" this carries the
    // body; for "photo"/"audio" it's an optional caption.
    body: z.string().max(5_000).optional(),
    // User-supplied note (ADR-0073's share-link proof-upload feature is
    // the first writer). Distinct field from `body` rather than a second
    // meaning for it — `body` is the legacy note-type payload; `caption`
    // is what new code writes going forward (see the DB column comment
    // in apps/core-api/src/db/schema.ts).
    // Nullable because the DB column is nullable — AI-authored evidence
    // rows and other write paths that never set a caption store a real
    // NULL, not just an absent key, so the schema must accept both.
    caption: z.string().max(4_000).nullable().optional(),
    // Provenance discriminator (ADR-0073). Optional here, mirroring the
    // DB column's NOT NULL DEFAULT 'ai' — a write path that omits it
    // still gets the correct default from Postgres, this schema just
    // stops forcing every caller to know that. Added 2026-08-13 so
    // packages/api-types' findingDetailEvidenceSchema (ADR-0085) can
    // .pick() it instead of carrying a wire-only .extend() exception.
    authorType: z.enum(["ai", "user", "provider"]).optional(),
    // #0454 (hilo de observaciones): the specific finding_events row this
    // evidence documents. null = "born with the observation" — correct
    // for every pre-existing row and for capture-time evidence, no
    // backfill (architecture-engineer ruling 2026-08-18). First writer:
    // the share-link /attend transaction, which journals the transition
    // and its proof photos atomically.
    findingEventId: z.string().uuid().nullable().optional(),
    createdAt: isoDateTimeSchema,
});
export const evidenceCreateSchema = evidenceSchema
    .omit({ id: true, createdAt: true })
    .extend({ id: evidenceIdSchema.optional() });
//# sourceMappingURL=evidence.js.map